// ── StoreSwitcher — V50 매장 전환 드롭다운 ──────────────────────────
// 매니저로 들어간 매장이 1개 이상일 때만 표시
// 본인 매장 (Owner) + 매니저로 관리하는 매장들 리스트

import React, { useState, useEffect, useRef } from 'react';
import { useLang } from '../i18n/LangContext';   // V147 중국어 화면

export default function StoreSwitcher({
  user,
  effectiveOwnerUid,
  isManagerMode,
  currentStore,
  managedStores,
  switchStore,
}) {
  const { lang } = useLang();
  const L = (ko, zh) => (lang === 'zh' ? zh : ko);
  const [open, setOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const onClick = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('click', onClick);
    return () => document.removeEventListener('click', onClick);
  }, []);

  // 매장이 본인 1개뿐이면 표시 안 함
  if (!managedStores || managedStores.length === 0) return null;

  const ownLabel = L('내 매장', '我的门店') + (user?.email ? ` (${user.email})` : '');
  // V98: 매장명 우선 표시 (없으면 이메일 fallback)
  const currentLabel = isManagerMode
    ? `👥 ${currentStore?.ownerStoreName || currentStore?.ownerEmail || effectiveOwnerUid?.slice(0, 8) + '...'}`
    : `🏪 ${L('내 매장', '我的门店')}`;

  return (
    <div ref={dropdownRef} style={{ position: 'relative', display: 'inline-block' }}>
      <button
        onClick={() => setOpen(o => !o)}
        style={{
          padding: '8px 14px', borderRadius: 8,
          border: '1.5px solid ' + (isManagerMode ? '#FF6F0F' : '#e5e7eb'),
          background: isManagerMode ? '#FAFAFA' : '#fff',
          color: isManagerMode ? '#9A3412' : '#1f2937',
          fontSize: 13, fontWeight: 700, cursor: 'pointer',
          display: 'flex', alignItems: 'center', gap: 6,
          maxWidth: 220,
          textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap',
        }}>
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{currentLabel}</span>
        <span style={{ fontSize: 10, color: '#9ca3af' }}>▼</span>
      </button>

      {open && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 4px)', left: 0,
          background: '#fff',
          border: '1px solid #e5e7eb', borderRadius: 8,
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
          minWidth: 260, maxWidth: 320, zIndex: 9999,
          overflow: 'hidden',
        }}>
          {/* 본인 매장 */}
          <StoreItem
            label={ownLabel}
            icon="🏪"
            active={!isManagerMode}
            onClick={() => { switchStore(user.uid); setOpen(false); }}
          />
          {/* 매니저 매장들 */}
          {managedStores.length > 0 && (
            <div style={{
              padding: '6px 12px', fontSize: 10, fontWeight: 700,
              color: '#9ca3af', background: '#f9fafb',
              borderTop: '1px solid #e5e7eb', borderBottom: '1px solid #e5e7eb',
            }}>
              {L('매니저로 관리 중', '我管理的门店')}
            </div>
          )}
          {managedStores.map(store => (
            <StoreItem
              key={store.ownerUid}
              label={store.ownerStoreName || store.ownerEmail || store.ownerUid.slice(0, 12) + '...'}
              sub={store.ownerStoreName ? store.ownerEmail : null}
              icon="👥"
              active={isManagerMode && currentStore?.ownerUid === store.ownerUid}
              onClick={() => { switchStore(store.ownerUid); setOpen(false); }}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function StoreItem({ label, sub, icon, active, onClick }) {
  return (
    <button
      onClick={onClick}
      style={{
        width: '100%', padding: '10px 14px',
        border: 'none', background: active ? '#FAFAFA' : '#fff',
        color: active ? '#9A3412' : '#1f2937',
        fontSize: 13, fontWeight: active ? 700 : 500,
        textAlign: 'left', cursor: 'pointer',
        display: 'flex', alignItems: 'center', gap: 8,
        borderBottom: '1px solid #f3f4f6',
      }}>
      <span style={{ fontSize: 16 }}>{icon}</span>
      <span style={{
        flex: 1, overflow: 'hidden', minWidth: 0,
      }}>
        <div style={{
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>{label}</div>
        {sub && (
          <div style={{
            fontSize: 11, color: '#9ca3af', fontWeight: 500, marginTop: 2,
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>{sub}</div>
        )}
      </span>
      {active && <span style={{ color: '#18181B', fontSize: 12 }}>✓</span>}
    </button>
  );
}
